import { useEffect, useState } from "react";
import { getMe } from "@/Api/user";
import { TypeUser } from "@/types/userType";

interface Permission {
  id: number;
  name: string;
  description: string;
  permission_group_id: number;
}

interface Role {
  id: number;
  uuid: string;
  name: string;
  permissions: Permission[];
}

interface User {
  id: number;
  uuid: string;
  name: string;
  email: string;
  user_type_id: number;
  roles: Role[];
}

interface UserContextType {
  user: User | null;
  loading: boolean;
  hasPermission: (permissionName: string) => boolean;
  hasAnyPermission: (permissionNames: string[]) => boolean;
  isAdmin: () => boolean;
  isTenantUser: () => boolean;
}

let globalUserData: User | null = null;
let globalLoading = false;
let subscribers: (() => void)[] = [];

const fetchUserData = async () => {
  try {
    globalLoading = true;
    notifySubscribers();

    const { data: response, ok } = await getMe();

    if (ok && response?.response) {
      globalUserData = response.response;
    } else {
      globalUserData = null;
    }
  } catch (error) {
    globalUserData = null;
  } finally {
    globalLoading = false;
    notifySubscribers();
  }
};

const notifySubscribers = () => {
  subscribers.forEach((callback) => callback());
};

const subscribe = (callback: () => void) => {
  subscribers.push(callback);
  return () => {
    subscribers = subscribers.filter((sub) => sub !== callback);
  };
};

export const hasPermission = (permissionName: string): boolean => {
  if (!globalUserData) return false;

  if (globalUserData.user_type_id == TypeUser.client || globalUserData.user_type_id == TypeUser.unity_responsible) {
    return (
      globalUserData.roles?.some((role) =>
        role.permissions?.some(
          (permission) => permission.name === permissionName
        )
      ) || false
    );
  }

  return true;
};

export const hasAnyPermission = (permissionNames: string[]): boolean => {
  if (!globalUserData) return false;

  if (globalUserData.user_type_id == 2) {
    return permissionNames.some((permissionName) =>
      hasPermission(permissionName)
    );
  }

  return true;
};

export const isAdmin = (): boolean => {
  return globalUserData?.user_type_id === 1;
};

export const isTenantUser = (): boolean => {
  return globalUserData?.user_type_id === 2;
};

export const usePermissions = (): UserContextType => {
  const [, setTrigger] = useState(0);

  useEffect(() => {
    const checkAndFetch = () => {
      const authData = JSON.parse(localStorage.getItem("auth") || "{}");
      if (authData.authorize && authData.token) {
        if (!globalUserData && !globalLoading) {
          fetchUserData();
        } else if (globalUserData) {
        }
      }
    };

    checkAndFetch();

    const timeoutId = setTimeout(() => {
      checkAndFetch();
    }, 500);

    const unsubscribe = subscribe(() => {
      setTrigger((prev) => prev + 1);
    });

    return () => {
      clearTimeout(timeoutId);
      unsubscribe();
    };
  }, []);

  return {
    user: globalUserData,
    loading: globalLoading,
    hasPermission,
    hasAnyPermission,
    isAdmin,
    isTenantUser,
  };
};

export const refreshUserData = () => {
  fetchUserData();
};

export const clearUserData = () => {
  globalUserData = null;
  globalLoading = false;
  notifySubscribers();
};
